import { defineMessages } from 'react-intl.macro';

import { useMessage } from '@tmapy/intl';

import type { ServerError, ServerErrors } from '../types';

import { BACKEND_VALIDATION_CODE } from '../constants';

export type ServerErrorItem = { isInvalid?: boolean; errorMessage?: string };
export type FormItemServerErrors = Record<string, ServerErrorItem>;

const serverErrorMessages = defineMessages<BACKEND_VALIDATION_CODE>({
  [BACKEND_VALIDATION_CODE.NULL]: {
    id: 'enum.BACKEND_VALIDATION_CODE.null',
    defaultMessage: 'Toto pole nesmí být prázdné.',
  },
  [BACKEND_VALIDATION_CODE.EMPTY]: {
    id: 'enum.BACKEND_VALIDATION_CODE.empty',
    defaultMessage: 'Toto pole nesmí být prázdné.',
  },
  [BACKEND_VALIDATION_CODE.INVALID]: {
    id: 'enum.BACKEND_VALIDATION_CODE.invalid',
    defaultMessage: '{message}',
  },
  [BACKEND_VALIDATION_CODE.UNIQUE]: {
    id: 'enum.BACKEND_VALIDATION_CODE.unique',
    defaultMessage: '{message}',
  },
  [BACKEND_VALIDATION_CODE.UPPER_CASE_MISSING]: {
    id: 'enum.BACKEND_VALIDATION_CODE.upper_case_missing',
    defaultMessage: '{message}',
  },
  [BACKEND_VALIDATION_CODE.INCORRECT_TYPE]: {
    id: 'enum.BACKEND_VALIDATION_CODE.incorrect_type',
    defaultMessage: '{message}',
  },
  [BACKEND_VALIDATION_CODE.NO_ACTIVE_ACCOUNT]: {
    id: 'no_active_account',
    defaultMessage: 'Uživatelský účet není aktivovaný.',
  },
  [BACKEND_VALIDATION_CODE.INVALID_CHOICE]: {
    id: 'invalid_choice',
    defaultMessage: '{message}',
  },
  [BACKEND_VALIDATION_CODE.UNKNOWN_SERVER_ERROR]: {
    id: 'unknown_server_error',
    defaultMessage: 'Neznámá chyba serveru.',
  },
});

export const UNKNOWN_SERVER_ERROR: ServerErrors = {
  non_field_errors: [
    {
      message: 'Unknown Server Error',
      code: BACKEND_VALIDATION_CODE.UNKNOWN_SERVER_ERROR,
    },
  ],
};

export const useFormItemServerErrors = (
  errors: ServerErrors | null | undefined,
): FormItemServerErrors => {
  const formatMessage = useMessage();
  const formItemServerErrors = {} as FormItemServerErrors;
  if (!errors) return formItemServerErrors;

  const getFieldErrorMessage = (fieldErrors: ServerError[]): string =>
    [
      ...new Set(
        fieldErrors.map((error) =>
          error.non_field_errors
            ? getFieldErrorMessage(error.non_field_errors)
            : error.code
              ? (formatMessage.lookup(serverErrorMessages, error.code, error as any) ??
                error.message)
              : error.message,
        ),
      ),
    ].join('\n');

  if (Array.isArray(errors)) {
    return { non_field_errors: { isInvalid: true, errorMessage: getFieldErrorMessage(errors) } };
  }

  if (errors.code) {
    return {
      non_field_errors: { isInvalid: true, errorMessage: getFieldErrorMessage([errors]) },
    };
  }

  if (typeof errors === 'object') {
    for (const [field, fieldErrors] of Object.entries(errors)) {
      formItemServerErrors[field] = {
        isInvalid: true,
        errorMessage: getFieldErrorMessage(fieldErrors),
      };
    }
  } else {
    console.error(
      '[useFormItemServerErrors]: invalid type of server error:',
      typeof errors,
      errors,
    );
    return {
      non_field_errors: {
        isInvalid: true,
        errorMessage: formatMessage(
          serverErrorMessages[BACKEND_VALIDATION_CODE.UNKNOWN_SERVER_ERROR],
        ),
      },
    };
  }

  return formItemServerErrors;
};
